import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import axios from 'axios';
import { storeContext } from '../provider/Provider';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles(() => ({
  card: {
    padding: 24,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: 20,
  },
  sectionHeading: {
    fontWeight: 700,
    fontSize: 20,
    color: '#58595B',
    marginBottom: 4,
  },
  sectionDesc: {
    fontSize: 13,
    color: '#999',
    marginBottom: 16,
  },
  clientRow: {
    background: '#f0fdf4',
    '& td': { fontWeight: 700 },
  },
  expandRow: {
    cursor: 'pointer',
    '&:hover': { background: '#fafafa' },
  },
  prosConsBox: {
    padding: '10px 16px',
    background: '#f8f9fa',
    borderRadius: 8,
  },
}));

function formatCount(n) {
  if (n === null || n === undefined || n === 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function getRankColor(rank) {
  if (rank <= 3) return '#10b981';
  if (rank <= 6) return '#f59e0b';
  return '#ef4444';
}

const CompetitorAnalysis = () => {
  const classes = useStyles();
  const { state } = useContext(storeContext);
  const domain = state.websiteSwitch.currentWebsite;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/competitor-analysis?limit=10`)
      .then((res) => {
        if (!res.data.error) setData(res.data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [domain]);

  if (!domain) return null;

  const toggleExpand = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const allRows = data ? [
    ...(data.client ? [{ ...data.client, isClient: true }] : []),
    ...(data.competitors || []).map((c) => ({ ...c, isClient: false })),
  ] : [];

  return (
    <>
      <Typography className={classes.sectionHeading} style={{ marginTop: 8 }}>
        Competitor Deep Dive
      </Typography>
      <Typography className={classes.sectionDesc}>
        Detailed comparison of the top firms appearing in AI responses for your tracked queries.
        Click a competitor to see strengths and weaknesses vs your firm.
      </Typography>

      <Paper className={classes.card}>
        {loading && <LinearProgress style={{ marginBottom: 16 }} />}

        {!loading && allRows.length === 0 && (
          <Typography color="textSecondary" style={{ textAlign: 'center', padding: 24 }}>
            Run a search first to generate competitor data.
          </Typography>
        )}

        {allRows.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999', width: 30 }}>#</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }}>Firm</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="center">Avg Rank</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="center">Best Rank</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="center">Appearances</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="center">Cities</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="center">Practice Areas</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="right">Ref. Domains</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }} align="right">Backlinks</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999', width: 30 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {allRows.map((row, idx) => (
                  <React.Fragment key={row.domain}>
                    <TableRow
                      className={row.isClient ? classes.clientRow : classes.expandRow}
                      onClick={() => !row.isClient && toggleExpand(idx)}
                    >
                      <TableCell>
                        <Typography style={{ fontSize: 12, fontWeight: 600, color: '#999' }}>
                          {idx + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography style={{ fontSize: 13, fontWeight: row.isClient ? 700 : 500, color: row.isClient ? '#0d9373' : '#333' }}>
                          {row.firm_name}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#999', fontSize: 11 }}>
                          {row.domain}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <span style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: 13,
                          color: getRankColor(row.avg_rank),
                        }}>
                          #{row.avg_rank}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <span style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: 13,
                          color: getRankColor(row.best_rank),
                        }}>
                          #{row.best_rank}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                          {row.appearances}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{row.cities}</span>
                      </TableCell>
                      <TableCell align="center">
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{row.practice_areas}</span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                          {formatCount(row.referring_domains)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {formatCount(row.total_backlinks)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {!row.isClient && (
                          expanded[idx]
                            ? <ExpandLessIcon style={{ color: '#999', fontSize: 18 }} />
                            : <ExpandMoreIcon style={{ color: '#999', fontSize: 18 }} />
                        )}
                      </TableCell>
                    </TableRow>

                    {!row.isClient && (
                      <TableRow>
                        <TableCell colSpan={10} style={{ padding: 0, border: 'none' }}>
                          <Collapse in={!!expanded[idx]}>
                            <Box className={classes.prosConsBox} m={1}>
                              <Box display="flex" style={{ gap: 24 }}>
                                {/* Strengths */}
                                <Box flex={1}>
                                  <Typography style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>
                                    Their Strengths (vs You)
                                  </Typography>
                                  {(row.strengths || []).length === 0 ? (
                                    <Typography variant="caption" color="textSecondary">None identified</Typography>
                                  ) : (
                                    row.strengths.map((s) => (
                                      <Box key={s} display="flex" alignItems="center" style={{ gap: 6 }} mb={0.5}>
                                        <span style={{ color: '#dc2626', fontSize: 12 }}>&#9650;</span>
                                        <Typography variant="caption" style={{ fontSize: 12 }}>{s}</Typography>
                                      </Box>
                                    ))
                                  )}
                                </Box>
                                {/* Weaknesses */}
                                <Box flex={1}>
                                  <Typography style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 6 }}>
                                    Their Weaknesses (vs You)
                                  </Typography>
                                  {(row.weaknesses || []).length === 0 ? (
                                    <Typography variant="caption" color="textSecondary">None identified</Typography>
                                  ) : (
                                    row.weaknesses.map((s) => (
                                      <Box key={s} display="flex" alignItems="center" style={{ gap: 6 }} mb={0.5}>
                                        <span style={{ color: '#16a34a', fontSize: 12 }}>&#9660;</span>
                                        <Typography variant="caption" style={{ fontSize: 12 }}>{s}</Typography>
                                      </Box>
                                    ))
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );
};

export default CompetitorAnalysis;
